import {
  axisIndex,
  axisPos,
  buildLayout,
  columnName,
  COLUMN_HEADER_HEIGHT,
  fontFamily,
  fontPx,
  type CellRange,
  type LayoutCell,
  type SheetLayout,
  type SizeOverrides,
} from './layout';
import { css, escapeHtml, renderPane, renderSelection } from './pane';
import { parseSpreadsheet, type CellLink, type Sheet } from './parser';
import { cellRect, cellText, copyText, rangeBetween, rangeText, type CellPoint, type SelectionView } from './selection';
import { STYLES } from './styles';
import { safeColor } from './parser/colors';

interface PaneRange {
  rowFrom: number;
  rowTo: number;
  colFrom: number;
  colTo: number;
}

interface Hit extends CellPoint {
  zone: 'cell' | 'col' | 'row' | 'corner';
  x: number;
  y: number;
}

interface Boundary {
  kind: 'col' | 'row';
  index: number;
}

interface Resize extends Boundary {
  start: number;
  origin: number;
  size: number;
}

interface Pane {
  area: HTMLElement;
  inner: HTMLElement;
  content: HTMLElement;
  selection: HTMLElement;
  frozenRows: boolean;
  frozenColumns: boolean;
}

const ROW_BUFFER = 20;
const COLUMN_BUFFER = 5;
const EXTRA_ROWS = 5;
const EXTRA_COLUMNS = 3;
const MAX_ROWS = 1048576;
const MAX_COLUMNS = 16384;
const AUTO_SCROLL_STEP = 20;
const LINK_HOLD_MS = 500;
const RESIZE_ZONE = 3;
const SAFE_LINK = /^(https?:|mailto:|ftp:)/i;

const TEXTS = {
  error: 'Не удалось открыть файл. Скачайте документ для просмотра.',
  empty: 'Нет данных для отображения',
  linkHint: 'Щелкните один раз, чтобы открыть. Щелкните и удерживайте, чтобы выделить эту ячейку.',
  linkWarning:
    'Гиперссылки могут быть опасны для компьютера и данных. Для защиты компьютера щелкайте только те гиперссылки, которые получены из надежных источников. Продолжить?',
  linkTitle: 'Переход по ссылке',
  yes: 'Да',
  no: 'Нет',
  width: 'Ширина',
  height: 'Высота',
};

const PANE_TEMPLATE = '<div class="xv__area"><div class="xv__inner"><div></div><div></div></div></div>';

const TEMPLATE = `
<div class="xv" data-state="empty">
  <div class="xv__message"></div>
  <div class="xv__sheet">
    <div class="xv__corner"></div>
    <div class="xv__area" data-ref="colHeadFrozen"></div>
    <div class="xv__area" data-ref="colHead"><div class="xv__inner"></div></div>
    <div class="xv__area" data-ref="rowHeadFrozen"></div>
    <div class="xv__area" data-ref="rowHead"><div class="xv__inner"></div></div>
    <div data-ref="panes">${PANE_TEMPLATE.repeat(4)}</div>
    <div class="xv__freeze" data-ref="freezeRow"></div>
    <div class="xv__freeze" data-ref="freezeCol"></div>
    <div class="xv__scroller" tabindex="0"><div></div></div>
    <textarea class="xv__cell-text" data-ref="cellText" readonly spellcheck="false"></textarea>
    <div class="xv__guide" data-ref="guide"></div>
    <div class="xv__size-tip" data-ref="sizeTip"></div>
    <div class="xv__dialog" data-ref="dialog">
      <div class="xv__dialog-box" role="dialog" aria-modal="true">
        <div class="xv__dialog-title">${TEXTS.linkTitle}</div>
        <div class="xv__dialog-text">${TEXTS.linkWarning}</div>
        <div class="xv__dialog-link" data-ref="dialogLink"></div>
        <div class="xv__dialog-buttons">
          <button type="button" class="xv__dialog-button" data-answer="yes">${TEXTS.yes}</button>
          <button type="button" class="xv__dialog-button" data-answer="no">${TEXTS.no}</button>
        </div>
      </div>
    </div>
  </div>
  <div class="xv__tabs"></div>
</div>`;

const ARROWS: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

const px = (value: number) => `${value}px`;

function place(element: HTMLElement, left: number, top: number, width: number, height: number): void {
  element.style.left = px(left);
  element.style.top = px(top);
  element.style.width = px(Math.max(width, 0));
  element.style.height = px(Math.max(height, 0));
}

export class ExcelViewerElement extends HTMLElement {
  private readonly shell: HTMLElement;
  private readonly message: HTMLElement;
  private readonly corner: HTMLElement;
  private readonly colHeadFrozen: HTMLElement;
  private readonly colHead: HTMLElement;
  private readonly rowHeadFrozen: HTMLElement;
  private readonly rowHead: HTMLElement;
  private readonly freezeRow: HTMLElement;
  private readonly freezeCol: HTMLElement;
  private readonly scroller: HTMLElement;
  private readonly sizer: HTMLElement;
  private readonly tabs: HTMLElement;
  private readonly panes: Pane[];
  private readonly cellTextField: HTMLTextAreaElement;
  private readonly guide: HTMLElement;
  private readonly sizeTip: HTMLElement;
  private readonly dialog: HTMLElement;
  private readonly dialogLink: HTMLElement;
  private readonly resizeObserver: ResizeObserver;

  private parsedSheets: Sheet[] = [];
  private activeSheetIndex = 0;
  private layouts = new Map<number, { layout: SheetLayout; urls: string[] }>();
  private overrides = new Map<number, SizeOverrides>();
  private layout: SheetLayout | null = null;
  private imageUrls: string[] = [];
  private imagesReleased = false;
  private zoom = 1;
  private viewport = { width: 0, height: 0 };
  private range: PaneRange = { rowFrom: 0, rowTo: 0, colFrom: 0, colTo: 0 };
  private extent = { rows: 0, cols: 0 };
  private viewX = 0;
  private viewY = 0;
  private headWidth = 0;
  private headHeight = 0;
  private frozenWidth = 0;
  private frozenHeight = 0;
  private openToken = 0;

  private active: CellPoint | null = null;
  private anchor: CellPoint | null = null;
  private focusPoint: CellPoint | null = null;
  private copied: CellRange | null = null;
  private dragMode: 'cell' | 'col' | 'row' | null = null;
  private pendingLink: { link: CellLink; timer: number } | null = null;
  private hoverState = { cursor: 'cell', tip: '' };
  private resize: Resize | null = null;
  private dialogUrl = '';

  constructor() {
    super();
    const root = this.attachShadow({ mode: 'open' });
    root.innerHTML = `<style>${STYLES}</style>${TEMPLATE}`;
    const ref = (name: string) => root.querySelector<HTMLElement>(`[data-ref="${name}"]`);
    this.shell = root.querySelector('.xv');
    this.message = root.querySelector('.xv__message');
    this.corner = root.querySelector('.xv__corner');
    this.colHeadFrozen = ref('colHeadFrozen');
    this.colHead = ref('colHead');
    this.rowHeadFrozen = ref('rowHeadFrozen');
    this.rowHead = ref('rowHead');
    this.freezeRow = ref('freezeRow');
    this.freezeCol = ref('freezeCol');
    this.scroller = root.querySelector('.xv__scroller');
    this.sizer = this.scroller.firstElementChild as HTMLElement;
    this.tabs = root.querySelector('.xv__tabs');
    this.panes = Array.from(ref('panes').children).map((area, index) => {
      const inner = area.firstElementChild as HTMLElement;
      return {
        area: area as HTMLElement,
        inner,
        content: inner.children[0] as HTMLElement,
        selection: inner.children[1] as HTMLElement,
        frozenRows: index < 2,
        frozenColumns: index % 2 === 0,
      };
    });

    this.cellTextField = ref('cellText') as HTMLTextAreaElement;
    this.guide = ref('guide');
    this.sizeTip = ref('sizeTip');
    this.dialog = ref('dialog');
    this.dialogLink = ref('dialogLink');

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.scroller.addEventListener('scroll', () => this.onScroll());
    this.scroller.addEventListener('mousedown', (event) => this.onMouseDown(event));
    this.scroller.addEventListener('mousemove', (event) => this.onHover(event));
    this.scroller.addEventListener('dblclick', (event) => this.onDoubleClick(event));
    this.scroller.addEventListener('keydown', (event) => this.onKeyDown(event));
    this.dialog.addEventListener('click', (event) => {
      const answer = (event.target as HTMLElement).closest<HTMLElement>('[data-answer]')?.dataset.answer;
      if (answer || event.target === this.dialog) {
        this.closeDialog(answer === 'yes');
      }
    });
    this.cellTextField.addEventListener('keydown', (event) => this.onCellTextKeyDown(event));
    this.cellTextField.addEventListener('blur', () => this.closeCellText(false));
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeDialog(false);
      }
    });
    this.tabs.addEventListener('click', (event) => {
      const tab = (event.target as HTMLElement).closest<HTMLElement>('[data-sheet]');
      if (tab) {
        this.showSheet(Number(tab.dataset.sheet));
      }
    });
  }

  connectedCallback(): void {
    this.resizeObserver.observe(this.scroller);
    if (this.imagesReleased) {
      this.restoreImages();
    }
    this.measure();
  }

  disconnectedCallback(): void {
    this.resizeObserver.disconnect();
    this.stopDrag();
    this.stopResize();
    queueMicrotask(() => {
      if (!this.isConnected) {
        this.releaseImages();
      }
    });
  }

  private releaseImages(): void {
    this.layouts.forEach((entry) => {
      entry.urls.forEach((url) => URL.revokeObjectURL(url));
      entry.urls = [];
    });
    this.imageUrls = [];
    this.imagesReleased = true;
  }

  private restoreImages(): void {
    this.imagesReleased = false;
    this.layouts.forEach((entry, index) => {
      entry.urls = (this.parsedSheets[index]?.images ?? []).map((image) => URL.createObjectURL(image.data));
    });
    this.imageUrls = this.layouts.get(this.activeSheetIndex)?.urls ?? [];
    if (this.layout) {
      this.renderPanes();
    }
  }

  get isOpened(): boolean {
    return this.shell.dataset.state === 'ready';
  }

  set isOpened(value: boolean) {
    if (!value) {
      this.close();
    }
  }

  get sheets(): Sheet[] {
    return this.parsedSheets;
  }

  async openExcelData(data: string | ArrayBuffer | Uint8Array | Blob): Promise<void> {
    const token = ++this.openToken;
    this.reset();
    this.setState('loading');
    try {
      const parsed = await parseSpreadsheet(data);
      if (token !== this.openToken) {
        return;
      }
      this.parsedSheets = parsed;
      if (!parsed.length) {
        this.setState('empty');
        return;
      }
      this.renderTabs();
      this.setState('ready');
      this.showSheet(
        Math.max(
          parsed.findIndex((sheet) => sheet.active),
          0,
        ),
      );
    } catch {
      if (token !== this.openToken) {
        return;
      }
      this.setState('error');
    }
  }

  close(): void {
    this.openToken++;
    this.reset();
    this.setState('empty');
  }

  clearContent(): void {
    this.close();
  }

  private reset(): void {
    this.stopDrag();
    this.layouts.forEach(({ urls }) => urls.forEach((url) => URL.revokeObjectURL(url)));
    this.layouts.clear();
    this.imagesReleased = false;
    this.overrides.clear();
    this.closeDialog(false);
    this.closeCellText(false);
    this.parsedSheets = [];
    this.layout = null;
    this.tabs.innerHTML = '';
    this.panes.forEach((pane) => {
      pane.content.innerHTML = '';
      pane.selection.innerHTML = '';
    });
  }

  private setState(state: 'loading' | 'error' | 'empty' | 'ready'): void {
    this.shell.dataset.state = state;
    if (state === 'loading') {
      this.message.innerHTML = '<div class="xv__spinner"></div>';
    } else if (state === 'error') {
      this.message.textContent = TEXTS.error;
    } else if (state === 'empty') {
      this.message.textContent = TEXTS.empty;
    }
    if (state === 'ready') {
      requestAnimationFrame(() => this.measure());
    }
  }

  private renderTabs(): void {
    this.tabs.innerHTML = this.parsedSheets
      .map((sheet, index) => {
        const tabColor = safeColor(sheet.tabColor);
        const style = tabColor ? ` style="${css({ borderBottomColor: tabColor })}"` : '';
        return `<button type="button" id="tab-excel-sheet-${index}" class="xv__tab" data-sheet="${index}"${style}>${escapeHtml(
          sheet.name,
        )}</button>`;
      })
      .join('');
  }

  private showSheet(index: number): void {
    const sheet = this.parsedSheets[index];
    if (!sheet) {
      return;
    }
    this.activeSheetIndex = index;
    let entry = this.layouts.get(index);
    if (!entry) {
      entry = {
        layout: buildLayout(sheet, this.overrides.get(index)),
        urls: sheet.images.map((image) => URL.createObjectURL(image.data)),
      };
      this.layouts.set(index, entry);
    }
    this.layout = entry.layout;
    this.imageUrls = entry.urls;
    this.zoom = sheet.zoom || 1;
    this.extent = { rows: entry.layout.extentRows + EXTRA_ROWS, cols: entry.layout.extentColumns + EXTRA_COLUMNS };
    this.tabs
      .querySelectorAll('.xv__tab')
      .forEach((tab, tabIndex) => tab.classList.toggle('xv__tab--active', tabIndex === index));

    const start = sheet.activeCell ?? { row: 0, col: 0 };
    this.active = start;
    this.anchor = start;
    this.focusPoint = start;
    this.copied = null;

    this.viewX = 0;
    this.viewY = 0;
    this.scroller.scrollLeft = 0;
    this.scroller.scrollTop = 0;
    this.relayout();
  }

  private measure(): void {
    const width = this.scroller.clientWidth;
    const height = this.scroller.clientHeight;
    if (width === this.viewport.width && height === this.viewport.height) {
      return;
    }
    this.viewport = { width, height };
    this.relayout();
  }

  private X(index: number): number {
    return Math.round(axisPos(this.layout.columns, index) * this.zoom);
  }

  private Y(index: number): number {
    return Math.round(axisPos(this.layout.rows, index) * this.zoom);
  }

  private get frozenRows(): number {
    return this.layout?.sheet.frozenRows ?? 0;
  }

  private get frozenColumns(): number {
    return this.layout?.sheet.frozenColumns ?? 0;
  }

  private get mainLeft(): number {
    return this.headWidth + this.frozenWidth;
  }

  private get mainTop(): number {
    return this.headHeight + this.frozenHeight;
  }

  private get mainWidth(): number {
    return Math.max(this.viewport.width - this.mainLeft, 0);
  }

  private get mainHeight(): number {
    return Math.max(this.viewport.height - this.mainTop, 0);
  }

  private relayout(): void {
    if (!this.layout) {
      return;
    }
    this.headWidth = Math.round(this.layout.rowHeaderWidth * this.zoom);
    this.headHeight = Math.round(COLUMN_HEADER_HEIGHT * this.zoom);
    this.frozenWidth = this.X(this.frozenColumns);
    this.frozenHeight = this.Y(this.frozenRows);

    place(this.corner, 0, 0, this.headWidth, this.headHeight);
    place(this.colHeadFrozen, this.headWidth, 0, this.frozenWidth, this.headHeight);
    place(this.colHead, this.mainLeft, 0, this.mainWidth, this.headHeight);
    place(this.rowHeadFrozen, 0, this.headHeight, this.headWidth, this.frozenHeight);
    place(this.rowHead, 0, this.mainTop, this.headWidth, this.mainHeight);

    this.panes.forEach((pane) => {
      const left = pane.frozenColumns ? this.headWidth : this.mainLeft;
      const top = pane.frozenRows ? this.headHeight : this.mainTop;
      const width = pane.frozenColumns ? this.frozenWidth : this.mainWidth;
      const height = pane.frozenRows ? this.frozenHeight : this.mainHeight;
      place(pane.area, left, top, width, height);
      pane.area.style.display = width > 0 && height > 0 ? '' : 'none';
    });

    place(this.freezeRow, 0, this.mainTop - 1, this.frozenHeight ? this.viewport.width : 0, 1);
    place(this.freezeCol, this.mainLeft - 1, 0, this.frozenWidth ? 1 : 0, this.mainTop + this.mainHeight);

    this.fitExtent();
    this.updateSizer();
    this.updateRange(true);
  }

  private updateSizer(): void {
    if (!this.layout) {
      return;
    }
    this.sizer.style.width = px(this.headWidth + this.X(Math.min(this.extent.cols, MAX_COLUMNS)));
    this.sizer.style.height = px(this.headHeight + this.Y(Math.min(this.extent.rows, MAX_ROWS)));
  }

  private visibleBounds(): PaneRange {
    const scale = this.zoom;
    const left = (this.frozenWidth + this.viewX) / scale;
    const top = (this.frozenHeight + this.viewY) / scale;
    return {
      colFrom: Math.max(this.frozenColumns, axisIndex(this.layout.columns, left)),
      colTo: axisIndex(this.layout.columns, left + this.mainWidth / scale) + 1,
      rowFrom: Math.max(this.frozenRows, axisIndex(this.layout.rows, top)),
      rowTo: axisIndex(this.layout.rows, top + this.mainHeight / scale) + 1,
    };
  }

  private fitExtent(): void {
    if (!this.layout) {
      return;
    }
    const scale = this.zoom;
    const cols = axisIndex(this.layout.columns, (this.frozenWidth + this.mainWidth * 1.5) / scale) + 1;
    const rows = axisIndex(this.layout.rows, (this.frozenHeight + this.mainHeight * 1.5) / scale) + 1;
    this.extent = {
      cols: Math.max(cols, this.extent.cols, this.layout.extentColumns + EXTRA_COLUMNS),
      rows: Math.max(rows, this.extent.rows, this.layout.extentRows + EXTRA_ROWS),
    };
  }

  private updateRange(force: boolean): void {
    if (!this.layout) {
      return;
    }
    const visible = this.visibleBounds();
    const rendered = this.range;
    const covered =
      visible.colFrom >= rendered.colFrom &&
      visible.colTo <= rendered.colTo &&
      visible.rowFrom >= rendered.rowFrom &&
      visible.rowTo <= rendered.rowTo;
    if (!covered || force) {
      this.range = {
        colFrom: Math.max(this.frozenColumns, visible.colFrom - COLUMN_BUFFER),
        colTo: visible.colTo + COLUMN_BUFFER,
        rowFrom: Math.max(this.frozenRows, visible.rowFrom - ROW_BUFFER),
        rowTo: visible.rowTo + ROW_BUFFER,
      };
      this.renderPanes();
      this.renderSelection();
    }
    this.applyTransforms();
  }

  private paneArea(pane: Pane) {
    return {
      rowFrom: pane.frozenRows ? 0 : this.range.rowFrom,
      rowTo: pane.frozenRows ? this.frozenRows : this.range.rowTo,
      colFrom: pane.frozenColumns ? 0 : this.range.colFrom,
      colTo: pane.frozenColumns ? this.frozenColumns : this.range.colTo,
    };
  }

  private renderPanes(): void {
    this.panes.forEach((pane) => {
      if (pane.area.style.display === 'none') {
        pane.content.innerHTML = '';
        return;
      }
      pane.content.innerHTML = renderPane({
        layout: this.layout,
        zoom: this.zoom,
        imageUrls: this.imageUrls,
        ...this.paneArea(pane),
      });
    });
  }

  private get selectionRange(): CellRange | null {
    if (!this.layout || !this.anchor || !this.focusPoint) {
      return null;
    }
    return rangeBetween(this.layout, this.anchor, this.focusPoint);
  }

  private renderSelection(): void {
    if (!this.layout) {
      return;
    }
    const range = this.selectionRange;
    const view: SelectionView | null =
      range && this.active ? { range, active: cellRect(this.layout, this.active), copied: this.copied } : null;
    const html = renderSelection(this.layout, this.zoom, view);
    this.panes.forEach((pane) => {
      pane.selection.innerHTML = pane.area.style.display === 'none' ? '' : html;
    });
    this.renderHeads(range);
  }

  private headsHtml(kind: 'col' | 'row', from: number, to: number, selected: CellRange | null): string {
    const fontSize = px(Math.round(11 * this.zoom));
    let html = '';
    for (let index = from; index < to; index++) {
      const start = kind === 'col' ? this.X(index) : this.Y(index);
      const size = (kind === 'col' ? this.X(index + 1) : this.Y(index + 1)) - start;
      if (size <= 0) {
        continue;
      }
      const isSelected =
        selected &&
        (kind === 'col'
          ? index >= selected.colFrom && index <= selected.colTo
          : index >= selected.rowFrom && index <= selected.rowTo);
      const style =
        kind === 'col'
          ? {
              left: px(start),
              top: '0',
              width: px(size),
              height: px(this.headHeight),
              lineHeight: px(this.headHeight),
              fontSize,
            }
          : { left: '0', top: px(start), width: px(this.headWidth), height: px(size), lineHeight: px(size), fontSize };
      const label = kind === 'col' ? columnName(index) : String(index + 1);
      html += `<div class="xv__head xv__head--${kind}${isSelected ? ' xv__head--selected' : ''}" style="${css(
        style,
      )}">${label}</div>`;
    }
    return html;
  }

  private renderHeads(selected: CellRange | null): void {
    this.colHeadFrozen.innerHTML = this.headsHtml('col', 0, this.frozenColumns, selected);
    (this.colHead.firstElementChild as HTMLElement).innerHTML = this.headsHtml(
      'col',
      this.range.colFrom,
      this.range.colTo,
      selected,
    );
    this.rowHeadFrozen.innerHTML = this.headsHtml('row', 0, this.frozenRows, selected);
    (this.rowHead.firstElementChild as HTMLElement).innerHTML = this.headsHtml(
      'row',
      this.range.rowFrom,
      this.range.rowTo,
      selected,
    );
  }

  private applyTransforms(): void {
    const x = this.frozenWidth + this.viewX;
    const y = this.frozenHeight + this.viewY;
    this.panes.forEach((pane) => {
      pane.inner.style.transform = `translate(${pane.frozenColumns ? 0 : -x}px, ${pane.frozenRows ? 0 : -y}px)`;
    });
    (this.colHead.firstElementChild as HTMLElement).style.transform = `translate(${-x}px, 0)`;
    (this.rowHead.firstElementChild as HTMLElement).style.transform = `translate(0, ${-y}px)`;
  }

  private onScroll(): void {
    this.viewX = this.scroller.scrollLeft;
    this.viewY = this.scroller.scrollTop;
    this.closeCellText(false);
    this.updateRange(false);
  }

  private hitTest(clientX: number, clientY: number, clamp: boolean): Hit | null {
    if (!this.layout) {
      return null;
    }
    const rect = this.scroller.getBoundingClientRect();
    let x = clientX - rect.left;
    let y = clientY - rect.top;
    if (clamp) {
      x = Math.min(Math.max(x, this.headWidth), this.viewport.width - 1);
      y = Math.min(Math.max(y, this.headHeight), this.viewport.height - 1);
    } else if (x >= this.viewport.width || y >= this.viewport.height) {
      return null;
    }

    const sheetX = x - this.headWidth;
    const sheetY = y - this.headHeight;
    const contentX = Math.max(sheetX < this.frozenWidth ? sheetX : sheetX + this.viewX, 0) / this.zoom;
    const contentY = Math.max(sheetY < this.frozenHeight ? sheetY : sheetY + this.viewY, 0) / this.zoom;
    const zone = sheetX < 0 && sheetY < 0 ? 'corner' : sheetX < 0 ? 'row' : sheetY < 0 ? 'col' : 'cell';
    return {
      zone,
      x: contentX,
      y: contentY,
      col: Math.min(axisIndex(this.layout.columns, contentX), MAX_COLUMNS - 1),
      row: Math.min(axisIndex(this.layout.rows, contentY), MAX_ROWS - 1),
    };
  }

  private layoutItem(point: CellPoint): LayoutCell | undefined {
    const rect = cellRect(this.layout, point);
    return (
      this.layout.rowItems[rect.rowFrom]?.find((item) => item.col === rect.colFrom) ??
      this.layout.mergeItems.find((item) => item.row === rect.rowFrom && item.col === rect.colFrom)
    );
  }

  private linkAt(hit: Hit): CellLink | null {
    if (hit.zone !== 'cell') {
      return null;
    }
    const rect = cellRect(this.layout, hit);
    const link = this.layout.sheet.rows[rect.rowFrom]?.[rect.colFrom]?.hyperlink;
    if (!link) {
      return null;
    }
    const text = this.layoutItem(hit)?.text;
    if (!text) {
      return null;
    }
    if (text.mode !== 'line') {
      return link;
    }
    const start = axisPos(this.layout.columns, text.clipFrom) + text.offset;
    return hit.x >= start && hit.x <= start + text.width ? link : null;
  }

  private selectCell(point: CellPoint): void {
    const rect = cellRect(this.layout, point);
    const target = { row: rect.rowFrom, col: rect.colFrom };
    this.active = target;
    this.anchor = target;
    this.focusPoint = target;
    this.renderSelection();
  }

  private selectAll(): void {
    this.anchor = { row: 0, col: 0 };
    this.focusPoint = { row: MAX_ROWS - 1, col: MAX_COLUMNS - 1 };
    if (!this.active) {
      this.active = { row: 0, col: 0 };
    }
    this.renderSelection();
  }

  private focusFor(hit: CellPoint): CellPoint {
    if (this.dragMode === 'col') {
      return { row: MAX_ROWS - 1, col: hit.col };
    }
    if (this.dragMode === 'row') {
      return { row: hit.row, col: MAX_COLUMNS - 1 };
    }
    return { row: hit.row, col: hit.col };
  }

  private readonly onWindowMouseMove = (event: MouseEvent): void => {
    if (!this.dragMode) {
      return;
    }
    const rect = this.scroller.getBoundingClientRect();
    if (event.clientY > rect.top + this.viewport.height) {
      this.scroller.scrollTop += AUTO_SCROLL_STEP;
    } else if (event.clientY < rect.top + this.mainTop && this.dragMode !== 'col') {
      this.scroller.scrollTop -= AUTO_SCROLL_STEP;
    }
    if (event.clientX > rect.left + this.viewport.width) {
      this.scroller.scrollLeft += AUTO_SCROLL_STEP;
    } else if (event.clientX < rect.left + this.mainLeft && this.dragMode !== 'row') {
      this.scroller.scrollLeft -= AUTO_SCROLL_STEP;
    }
    this.onScroll();
    const hit = this.hitTest(event.clientX, event.clientY, true);
    if (!hit) {
      return;
    }
    this.focusPoint = this.focusFor(hit);
    this.renderSelection();
  };

  private readonly onWindowMouseUp = (): void => {
    if (this.pendingLink) {
      window.clearTimeout(this.pendingLink.timer);
      const { link } = this.pendingLink;
      this.pendingLink = null;
      this.followLink(link);
    }
    this.stopDrag();
  };

  private stopDrag(): void {
    if (this.pendingLink) {
      window.clearTimeout(this.pendingLink.timer);
    }
    this.pendingLink = null;
    this.dragMode = null;
    window.removeEventListener('mousemove', this.onWindowMouseMove);
    window.removeEventListener('mouseup', this.onWindowMouseUp);
  }

  private startCellSelection(hit: CellPoint, extend: boolean): void {
    this.dragMode = 'cell';
    if (extend && this.anchor) {
      this.focusPoint = { row: hit.row, col: hit.col };
      this.renderSelection();
    } else {
      this.selectCell(hit);
    }
  }

  private onMouseDown(event: MouseEvent): void {
    if (event.button !== 0 || !this.layout) {
      return;
    }
    const hit = this.hitTest(event.clientX, event.clientY, false);
    if (!hit) {
      return;
    }
    event.preventDefault();
    this.scroller.focus({ preventScroll: true });
    const boundary = this.boundaryAt(hit);
    if (boundary) {
      this.startResize(boundary, event);
      return;
    }
    window.addEventListener('mousemove', this.onWindowMouseMove);
    window.addEventListener('mouseup', this.onWindowMouseUp);

    if (hit.zone === 'corner') {
      this.selectAll();
      return;
    }
    if (hit.zone === 'col' || hit.zone === 'row') {
      this.dragMode = hit.zone;
      const visible = this.visibleBounds();
      if (hit.zone === 'col') {
        const col = event.shiftKey && this.anchor ? this.anchor.col : hit.col;
        if (!event.shiftKey) {
          this.active = { row: this.frozenRows ? 0 : visible.rowFrom, col };
        }
        this.anchor = { row: 0, col };
      } else {
        const row = event.shiftKey && this.anchor ? this.anchor.row : hit.row;
        if (!event.shiftKey) {
          this.active = { row, col: this.frozenColumns ? 0 : visible.colFrom };
        }
        this.anchor = { row, col: 0 };
      }
      this.focusPoint = this.focusFor(hit);
      this.renderSelection();
      return;
    }

    const link = event.shiftKey ? null : this.linkAt(hit);
    if (link) {
      const timer = window.setTimeout(() => {
        this.pendingLink = null;
        this.startCellSelection(hit, false);
        this.setHover('cell', '');
      }, LINK_HOLD_MS);
      this.pendingLink = { link, timer };
      return;
    }
    this.startCellSelection(hit, event.shiftKey);
  }

  private setHover(cursor: string, tip: string): void {
    if (cursor === this.hoverState.cursor && tip === this.hoverState.tip) {
      return;
    }
    this.hoverState = { cursor, tip };
    this.scroller.style.cursor = cursor;
    this.scroller.title = tip;
  }

  private onHover(event: MouseEvent): void {
    if (this.dragMode || this.pendingLink || !this.layout) {
      return;
    }
    const hit = this.hitTest(event.clientX, event.clientY, false);
    const boundary = hit ? this.boundaryAt(hit) : null;
    if (boundary) {
      this.setHover(boundary.kind === 'col' ? 'col-resize' : 'row-resize', '');
      return;
    }
    const link = hit ? this.linkAt(hit) : null;
    if (link) {
      const target = link.tooltip ?? link.url ?? link.location ?? '';
      this.setHover('pointer', `${target} - ${TEXTS.linkHint}`);
    } else {
      this.setHover(hit && hit.zone !== 'cell' ? 'default' : 'cell', '');
    }
  }

  private followLink(link: CellLink): void {
    if (link.location) {
      this.goToLocation(link.location);
      return;
    }
    if (!link.url) {
      return;
    }
    const url = /^www\./i.test(link.url) ? `http://${link.url}` : link.url;
    if (SAFE_LINK.test(url)) {
      this.openDialog(url);
    }
  }

  private openDialog(url: string): void {
    this.dialogUrl = url;
    this.dialogLink.textContent = url;
    this.dialog.classList.add('xv__dialog--open');
    this.dialog.querySelector<HTMLElement>('[data-answer="yes"]')?.focus();
  }

  private closeDialog(confirmed: boolean): void {
    if (!this.dialog.classList.contains('xv__dialog--open')) {
      return;
    }
    const url = this.dialogUrl;
    this.dialogUrl = '';
    this.dialog.classList.remove('xv__dialog--open');
    this.scroller.focus({ preventScroll: true });
    if (confirmed && url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  private boundaryAt(hit: Hit): Boundary | null {
    if (hit.zone === 'col') {
      const x = hit.x * this.zoom;
      if (Math.abs(this.X(hit.col + 1) - x) <= RESIZE_ZONE) {
        return { kind: 'col', index: hit.col };
      }
      if (hit.col > 0 && Math.abs(this.X(hit.col) - x) <= RESIZE_ZONE) {
        return { kind: 'col', index: hit.col - 1 };
      }
    }
    if (hit.zone === 'row') {
      const y = hit.y * this.zoom;
      if (Math.abs(this.Y(hit.row + 1) - y) <= RESIZE_ZONE) {
        return { kind: 'row', index: hit.row };
      }
      if (hit.row > 0 && Math.abs(this.Y(hit.row) - y) <= RESIZE_ZONE) {
        return { kind: 'row', index: hit.row - 1 };
      }
    }
    return null;
  }

  private edgeOnScreen(kind: 'col' | 'row', index: number): number {
    if (kind === 'col') {
      return this.headWidth + this.X(index) - (index >= this.frozenColumns ? this.viewX : 0);
    }
    return this.headHeight + this.Y(index) - (index >= this.frozenRows ? this.viewY : 0);
  }

  private startResize(boundary: Boundary, event: MouseEvent): void {
    const { kind, index } = boundary;
    const size = kind === 'col' ? this.X(index + 1) - this.X(index) : this.Y(index + 1) - this.Y(index);
    this.resize = {
      ...boundary,
      start: kind === 'col' ? event.clientX : event.clientY,
      origin: this.edgeOnScreen(kind, index),
      size,
    };
    this.showGuide(size);
    window.addEventListener('mousemove', this.onResizeMove);
    window.addEventListener('mouseup', this.onResizeUp);
  }

  private showGuide(size: number): void {
    const resize = this.resize;
    const edge = resize.origin + size;
    if (resize.kind === 'col') {
      place(this.guide, edge, 0, 1, this.viewport.height);
      this.sizeTip.style.left = px(edge + 6);
      this.sizeTip.style.top = px(2);
    } else {
      place(this.guide, 0, edge, this.viewport.width, 1);
      this.sizeTip.style.left = px(2);
      this.sizeTip.style.top = px(edge + 6);
    }
    this.sizeTip.textContent = this.sizeText(resize.kind, size);
    this.guide.style.display = 'block';
    this.sizeTip.style.display = 'block';
  }

  private sizeText(kind: 'col' | 'row', size: number): string {
    const pixels = Math.round(size / this.zoom);
    const lastTwo = pixels % 100;
    const last = pixels % 10;
    const unit =
      last === 1 && lastTwo !== 11
        ? 'пиксель'
        : last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)
          ? 'пикселя'
          : 'пикселей';
    const decimal = (value: number) => value.toFixed(2).replace('.', ',');
    if (kind === 'row') {
      return `${TEXTS.height}: ${decimal(pixels * 0.75)} (${pixels} ${unit})`;
    }
    const digit = this.layout.digitWidth;
    const chars = pixels > digit + 5 ? Math.trunc(((pixels - 5) / digit) * 100 + 0.5) / 100 : pixels / (digit + 5);
    return `${TEXTS.width}: ${decimal(chars)} (${pixels} ${unit})`;
  }

  private readonly onResizeMove = (event: MouseEvent): void => {
    const resize = this.resize;
    if (!resize) {
      return;
    }
    const delta = (resize.kind === 'col' ? event.clientX : event.clientY) - resize.start;
    this.showGuide(Math.max(resize.size + delta, 0));
  };

  private readonly onResizeUp = (event: MouseEvent): void => {
    const resize = this.resize;
    this.stopResize();
    if (!resize) {
      return;
    }
    const delta = (resize.kind === 'col' ? event.clientX : event.clientY) - resize.start;
    if (!delta) {
      return;
    }
    this.applySize(resize, Math.max(resize.size + delta, 0) / this.zoom);
  };

  private stopResize(): void {
    this.resize = null;
    this.guide.style.display = 'none';
    this.sizeTip.style.display = 'none';
    window.removeEventListener('mousemove', this.onResizeMove);
    window.removeEventListener('mouseup', this.onResizeUp);
  }

  private resizeTargets(boundary: Boundary): number[] {
    const selected = this.selectionRange;
    const wholeColumns = selected && selected.rowFrom === 0 && selected.rowTo === MAX_ROWS - 1;
    const wholeRows = selected && selected.colFrom === 0 && selected.colTo === MAX_COLUMNS - 1;
    let from = boundary.index;
    let to = boundary.index;
    if (boundary.kind === 'col' && wholeColumns && from >= selected.colFrom && from <= selected.colTo) {
      from = selected.colFrom;
      to = Math.min(selected.colTo, this.extent.cols);
    }
    if (boundary.kind === 'row' && wholeRows && from >= selected.rowFrom && from <= selected.rowTo) {
      from = selected.rowFrom;
      to = Math.min(selected.rowTo, this.extent.rows);
    }
    const targets: number[] = [];
    for (let index = from; index <= to; index++) {
      targets.push(index);
    }
    return targets;
  }

  private applySize(boundary: Boundary, size: number | 'auto'): void {
    const sheet = this.parsedSheets[this.activeSheetIndex];
    const entry = this.layouts.get(this.activeSheetIndex);
    if (!sheet || !entry) {
      return;
    }
    let overrides = this.overrides.get(this.activeSheetIndex);
    if (!overrides) {
      overrides = { columns: new Map(), rows: new Map() };
      this.overrides.set(this.activeSheetIndex, overrides);
    }
    const target = boundary.kind === 'col' ? overrides.columns : overrides.rows;
    this.resizeTargets(boundary).forEach((index) => target.set(index, size));
    entry.layout = buildLayout(sheet, overrides);
    this.layout = entry.layout;
    this.relayout();
  }

  private onDoubleClick(event: MouseEvent): void {
    const hit = this.hitTest(event.clientX, event.clientY, false);
    const boundary = hit ? this.boundaryAt(hit) : null;
    if (boundary) {
      this.applySize(boundary, 'auto');
      return;
    }
    if (hit?.zone !== 'cell') {
      return;
    }
    if (this.pendingLink) {
      window.clearTimeout(this.pendingLink.timer);
      this.pendingLink = null;
    }
    this.selectCell(hit);
    this.openCellText();
  }

  private openCellText(): void {
    if (!this.layout || !this.active) {
      return;
    }
    this.ensureVisible(this.active);
    const rect = cellRect(this.layout, this.active);
    const sheet = this.layout.sheet;
    const cell = sheet.rows[rect.rowFrom]?.[rect.colFrom];
    const font = { fontName: 'Calibri', fontSize: 11, ...sheet.defaultFont, ...cell?.style };
    const left = this.edgeOnScreen('col', rect.colFrom);
    const top = this.edgeOnScreen('row', rect.rowFrom);
    const width = this.X(rect.colTo + 1) - this.X(rect.colFrom) + 1;
    const height = this.Y(rect.rowTo + 1) - this.Y(rect.rowFrom) + 1;
    const field = this.cellTextField;
    Object.assign(field.style, {
      display: 'block',
      left: px(left),
      top: px(top),
      width: cell?.style?.wrap ? px(width) : '',
      minWidth: px(width),
      minHeight: px(height),
      maxWidth: px(Math.max(this.viewport.width - left, width)),
      maxHeight: px(Math.max(this.viewport.height - top, height)),
      whiteSpace: cell?.style?.wrap ? 'pre-wrap' : 'pre',
      fontFamily: fontFamily(font.fontName),
      fontSize: px(fontPx(font) * this.zoom),
      fontWeight: font.bold ? '700' : '400',
      fontStyle: font.italic ? 'italic' : 'normal',
      color: font.color ?? '#000000',
    });
    field.value = cellText(cell, Boolean(sheet.date1904));
    field.focus({ preventScroll: true });
    field.setSelectionRange(field.value.length, field.value.length);
  }

  private closeCellText(refocus: boolean): void {
    if (this.cellTextField.style.display !== 'block') {
      return;
    }
    this.cellTextField.style.display = 'none';
    this.cellTextField.value = '';
    if (refocus) {
      this.scroller.focus({ preventScroll: true });
    }
  }

  private onCellTextKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeCellText(true);
    } else if (event.key === 'Enter' && !event.altKey) {
      event.preventDefault();
      this.closeCellText(true);
      this.moveInsideSelection(event.shiftKey ? -1 : 1, 0);
    } else if (event.key === 'Tab') {
      event.preventDefault();
      this.closeCellText(true);
      this.moveInsideSelection(0, event.shiftKey ? -1 : 1);
    }
  }

  private goToLocation(target: string): void {
    const location = target.replace(/^#/, '');
    const bang = location.lastIndexOf('!');
    const sheetName = bang >= 0 ? location.slice(0, bang).replace(/^'|'$/g, '').replace(/''/g, "'") : '';
    const match = (bang >= 0 ? location.slice(bang + 1) : location).replace(/\$/g, '').match(/^([A-Za-z]{1,3})(\d+)/);
    if (sheetName) {
      const index = this.parsedSheets.findIndex((sheet) => sheet.name === sheetName);
      if (index < 0) {
        return;
      }
      if (index !== this.activeSheetIndex) {
        this.showSheet(index);
      }
    }
    if (!match || !this.layout) {
      return;
    }
    let col = 0;
    for (const letter of match[1].toUpperCase()) {
      col = col * 26 + (letter.charCodeAt(0) - 64);
    }
    const point = { row: Number(match[2]) - 1, col: col - 1 };
    this.selectCell(point);
    this.ensureVisible(point);
  }

  private ensureVisible(point: CellPoint): void {
    if (!this.layout) {
      return;
    }
    const rect = cellRect(this.layout, point);
    if (rect.colTo + EXTRA_COLUMNS > this.extent.cols || rect.rowTo + EXTRA_ROWS > this.extent.rows) {
      this.extent = {
        cols: Math.max(this.extent.cols, rect.colTo + EXTRA_COLUMNS),
        rows: Math.max(this.extent.rows, rect.rowTo + EXTRA_ROWS),
      };
      this.updateSizer();
    }
    if (rect.colFrom >= this.frozenColumns) {
      const left = this.X(rect.colFrom) - this.frozenWidth;
      const right = this.X(rect.colTo + 1) - this.frozenWidth;
      if (left < this.viewX) {
        this.scroller.scrollLeft = left;
      } else if (right > this.viewX + this.mainWidth) {
        this.scroller.scrollLeft = Math.min(left, right - this.mainWidth);
      }
    }
    if (rect.rowFrom >= this.frozenRows) {
      const top = this.Y(rect.rowFrom) - this.frozenHeight;
      const bottom = this.Y(rect.rowTo + 1) - this.frozenHeight;
      if (top < this.viewY) {
        this.scroller.scrollTop = top;
      } else if (bottom > this.viewY + this.mainHeight) {
        this.scroller.scrollTop = Math.min(top, bottom - this.mainHeight);
      }
    }
    this.onScroll();
  }

  private step(axis: 'row' | 'col', from: number, delta: number): number {
    const hidden = axis === 'row' ? this.layout.sheet.hiddenRows : this.layout.sheet.hiddenColumns;
    const limit = (axis === 'row' ? MAX_ROWS : MAX_COLUMNS) - 1;
    let index = from;
    do {
      index += delta;
    } while (index >= 0 && index <= limit && hidden[index]);
    return index < 0 || index > limit ? from : index;
  }

  private move(point: CellPoint, rows: number, cols: number): CellPoint {
    const rect = cellRect(this.layout, point);
    let { row, col } = point;
    if (rows > 0) {
      row = this.step('row', rect.rowTo, 1);
    }
    if (rows < 0) {
      row = this.step('row', rect.rowFrom, -1);
    }
    if (cols > 0) {
      col = this.step('col', rect.colTo, 1);
    }
    if (cols < 0) {
      col = this.step('col', rect.colFrom, -1);
    }
    return { row, col };
  }

  private isFilled(row: number, col: number): boolean {
    const cell = this.layout.sheet.rows[row]?.[col];
    return Boolean(cell && cell.kind !== 'empty' && cell.text !== '');
  }

  private jump(point: CellPoint, rows: number, cols: number): CellPoint {
    const lastRow = Math.max(this.layout.usedRows - 1, point.row);
    const lastCol = Math.max(this.layout.usedColumns - 1, point.col);
    const inside = (row: number, col: number) => row >= 0 && col >= 0 && row <= lastRow && col <= lastCol;
    let row = point.row + rows;
    let col = point.col + cols;
    if (!inside(row, col)) {
      return point;
    }
    if (this.isFilled(point.row, point.col) && this.isFilled(row, col)) {
      while (inside(row + rows, col + cols) && this.isFilled(row + rows, col + cols)) {
        row += rows;
        col += cols;
      }
      return { row, col };
    }
    while (inside(row + rows, col + cols) && !this.isFilled(row, col)) {
      row += rows;
      col += cols;
    }
    return { row, col };
  }

  private moveInsideSelection(rows: number, cols: number): void {
    const selected = this.selectionRange;
    const current = this.active;
    const rect = cellRect(this.layout, current);
    const isSingle =
      !selected ||
      (rect.rowFrom === selected.rowFrom &&
        rect.rowTo === selected.rowTo &&
        rect.colFrom === selected.colFrom &&
        rect.colTo === selected.colTo);
    if (isSingle) {
      const target = this.move(current, rows, cols);
      this.selectCell(target);
      this.ensureVisible(target);
      return;
    }
    let { row, col } = current;
    if (cols) {
      col += cols;
      if (col > selected.colTo) {
        col = selected.colFrom;
        row = row + 1 > selected.rowTo ? selected.rowFrom : row + 1;
      } else if (col < selected.colFrom) {
        col = selected.colTo;
        row = row - 1 < selected.rowFrom ? selected.rowTo : row - 1;
      }
    } else {
      row += rows;
      if (row > selected.rowTo) {
        row = selected.rowFrom;
        col = col + 1 > selected.colTo ? selected.colFrom : col + 1;
      } else if (row < selected.rowFrom) {
        row = selected.rowTo;
        col = col - 1 < selected.colFrom ? selected.colTo : col - 1;
      }
    }
    this.active = { row, col };
    this.renderSelection();
    this.ensureVisible(this.active);
  }

  private moveTo(target: CellPoint): void {
    this.selectCell(target);
    this.ensureVisible(target);
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (!this.layout || !this.active) {
      return;
    }
    const ctrl = event.ctrlKey || event.metaKey;
    const arrow = ARROWS[event.key];
    const selected = this.selectionRange;

    if (arrow) {
      event.preventDefault();
      const [rows, cols] = arrow;
      if (event.shiftKey) {
        const from = this.focusPoint ?? this.active;
        this.focusPoint = ctrl ? this.jump(from, rows, cols) : this.move(from, rows, cols);
        this.renderSelection();
        this.ensureVisible(this.focusPoint);
      } else {
        this.moveTo(ctrl ? this.jump(this.active, rows, cols) : this.move(this.active, rows, cols));
      }
      return;
    }

    if (ctrl && event.code === 'KeyC') {
      event.preventDefault();
      if (!selected) {
        return;
      }
      copyText(rangeText(this.layout, selected));
      this.copied = selected;
      this.renderSelection();
      return;
    }
    if (ctrl && event.code === 'KeyA') {
      event.preventDefault();
      this.selectAll();
      return;
    }
    if (event.code === 'Space' && (ctrl || event.shiftKey) && selected) {
      event.preventDefault();
      if (ctrl) {
        this.anchor = { row: 0, col: selected.colFrom };
        this.focusPoint = { row: MAX_ROWS - 1, col: selected.colTo };
      } else {
        this.anchor = { row: selected.rowFrom, col: 0 };
        this.focusPoint = { row: selected.rowTo, col: MAX_COLUMNS - 1 };
      }
      this.renderSelection();
      return;
    }

    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        this.moveInsideSelection(event.shiftKey ? -1 : 1, 0);
        break;
      case 'Tab':
        event.preventDefault();
        this.moveInsideSelection(0, event.shiftKey ? -1 : 1);
        break;
      case 'Home':
        event.preventDefault();
        this.moveTo(ctrl ? { row: this.frozenRows, col: this.frozenColumns } : { row: this.active.row, col: 0 });
        break;
      case 'End':
        if (!ctrl) {
          break;
        }
        event.preventDefault();
        this.moveTo({ row: Math.max(this.layout.usedRows - 1, 0), col: Math.max(this.layout.usedColumns - 1, 0) });
        break;
      case 'PageDown':
      case 'PageUp': {
        event.preventDefault();
        const direction = event.key === 'PageDown' ? 1 : -1;
        const visible = this.visibleBounds();
        const pageRows = Math.max(visible.rowTo - visible.rowFrom - 1, 1);
        const row = Math.min(Math.max(this.active.row + direction * pageRows, 0), MAX_ROWS - 1);
        this.scroller.scrollTop += direction * this.mainHeight;
        this.moveTo({ row, col: this.active.col });
        break;
      }
      case 'F2':
        event.preventDefault();
        this.openCellText();
        break;
      case 'Escape':
        this.copied = null;
        this.renderSelection();
        break;
    }
  }
}

export function defineExcelViewer(tagName = 'excel-viewer'): void {
  if (typeof customElements !== 'undefined' && !customElements.get(tagName)) {
    customElements.define(tagName, ExcelViewerElement);
  }
}
